import { Loader2, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

import { isNavigableHref } from '@/utils/url-utils';
import { useEffect, useRef, useState } from 'react';

const COLLAPSED_MAX_HEIGHT = 180;

interface SearchSummaryProps {
  summary: string;
  isStreaming: boolean;
  status?: string;
  linkColor: string;
  fontColor: string;
  primaryColor: string;
  onDiveDeeper: () => void;
  borderRadius?: string;
}

export const SearchSummary = ({
  summary,
  isStreaming,
  status,
  linkColor,
  fontColor,
  primaryColor,
  onDiveDeeper,
  borderRadius = '8px',
}: SearchSummaryProps) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!contentRef.current) return;
    setOverflows(contentRef.current.scrollHeight > COLLAPSED_MAX_HEIGHT);
  }, [summary]);

  const needsFade = overflows && !expanded;

  return (
    <div
      className='border p-4'
      style={{
        borderColor: primaryColor + '30',
        background: `linear-gradient(135deg, ${primaryColor}08, ${primaryColor}04)`,
        borderRadius,
      }}
    >
      <div className='flex items-center gap-2 mb-3'>
        <Sparkles className='size-4' style={{ color: primaryColor }} />
        <span
          className='text-xs font-semibold uppercase tracking-wide'
          style={{ color: primaryColor }}
        >
          AI Summary
        </span>
      </div>

      {status && !summary && (
        <div className='flex items-center gap-2 py-1'>
          <Loader2 className='size-4 animate-spin opacity-50' />
          <span className='text-xs italic opacity-60'>{status}</span>
        </div>
      )}

      {summary && (
        <div className='relative'>
          <div
            ref={contentRef}
            className='prose prose-sm max-w-none overflow-hidden [&>*:first-child]:mt-0 [&>*:last-child]:mb-0'
            style={{
              maxHeight: expanded ? undefined : COLLAPSED_MAX_HEIGHT,
            }}
          >
            <ReactMarkdown
              components={{
                // A destination react-markdown blanked, or one that resolves
                // off this origin, is not a link: rendering it as an anchor
                // would reopen the current page or jump off-site.
                a: ({ href, children }) =>
                  isNavigableHref(href) ? (
                    <a
                      href={href}
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{
                        color: linkColor,
                        textDecorationColor: linkColor + '50',
                      }}
                      className='underline underline-offset-2 hover:opacity-80'
                    >
                      {children}
                    </a>
                  ) : (
                    <span>{children}</span>
                  ),
              }}
            >
              {summary}
            </ReactMarkdown>
          </div>

          {needsFade && (
            <div
              className='pointer-events-none absolute inset-x-0 bottom-0 h-16'
              style={{
                background: `linear-gradient(to top, ${primaryColor}0a, transparent)`,
              }}
            />
          )}
        </div>
      )}

      {isStreaming && summary && (
        <span className='mt-1 inline-block size-1.5 animate-pulse rounded-full bg-gray-400 align-middle' />
      )}

      <div className='mt-3 flex items-center gap-2'>
        {overflows && (
          <button
            onClick={() => setExpanded(!expanded)}
            className='border px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-opacity hover:opacity-80'
            style={{
              borderColor: fontColor + '20',
              color: fontColor + 'cc',
              borderRadius,
            }}
          >
            {expanded ? 'Show less' : 'Read more'}
          </button>
        )}

        {!isStreaming && summary && (
          <button
            onClick={onDiveDeeper}
            className='px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90'
            style={{ backgroundColor: linkColor, borderRadius }}
          >
            Dive deeper
          </button>
        )}
      </div>
    </div>
  );
};
